import type {
	GenericFilters,
	GroupedFilters,
	PriceFilter,
	SelectedFilter
} from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { GroupedParametrics } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";

/** One filter group's options (or the price range) as norce-pwa groups them. */
export type FilterSectionValue =
	| NonNullable<GenericFilters[keyof GenericFilters]>
	| PriceFilter
	| GroupedParametrics[string];

/**
 * Stands for every parametric group (Norce names them after the parametric's
 * code, so a template cannot list them before the client creates them).
 */
export const PARAMETRICS_SECTION = "parametrics";

/** A selection key (the auto-import is the enum object, not its type). */
type FilterTypeKey = (typeof FilterTypes)[keyof typeof FilterTypes];

/**
 * Group names norce-pwa gives its built-in filters (its `Filters` enum), with
 * the key each keeps its selection under. `color` is not here: Norce selects
 * colours as parametrics.
 */
const BUILT_IN_SELECTION_KEYS = new Map<string, FilterTypeKey | undefined>([
	["category", FilterTypes.CATEGORY],
	["price", FilterTypes.PRICE],
	["brands", FilterTypes.MANUFACTURER],
	["stock", FilterTypes.ONHAND],
	["flags", FilterTypes.FLAG],
	["color", undefined]
]);

const isParametricSection = (name: string): boolean =>
	!BUILT_IN_SELECTION_KEYS.has(name);

const matchesSection = (name: string, listed: string): boolean =>
	listed === name ||
	(listed === PARAMETRICS_SECTION && isParametricSection(name));

/**
 * The listing's filter groups as `[name, filter]` pairs: those named in
 * `order` first (in that order), then the rest in Norce's order. Empty groups
 * are left out.
 */
export const orderFilterSections = (
	filters: GroupedFilters | undefined,
	order: readonly string[]
): [string, FilterSectionValue][] => {
	const sections = Object.entries(filters ?? {}).filter(
		(entry): entry is [string, FilterSectionValue] => !!entry[1]
	);
	// A group's own name outranks the parametrics token that also covers it.
	const rank = (name: string) => {
		const own = order.indexOf(name);
		if (own !== -1) return own;
		const index = order.findIndex((listed) => matchesSection(name, listed));
		return index === -1 ? order.length : index;
	};
	// Array sort is stable: equal ranks keep Norce's order.
	return [...sections].sort(([a], [b]) => rank(a) - rank(b));
};

/** A group the template wants open on load, selected or not. */
export const isFilterSectionOpenByDefault = (
	name: string,
	openSections: readonly string[]
): boolean => openSections.some((listed) => matchesSection(name, listed));

/**
 * The group has a selected value, so its section starts open. Built-in groups
 * have their own key; parametric groups share one, matched by the ids of
 * the parametrics among their options.
 */
export const isFilterSectionSelected = (
	name: string,
	options: FilterSectionValue | undefined,
	selected: SelectedFilter | undefined
): boolean => {
	if (!selected) return false;
	const groupKey = BUILT_IN_SELECTION_KEYS.get(name);
	if (groupKey) return !!selected[groupKey];

	const parametrics = selected[FilterTypes.PARAMETRIC];
	if (!parametrics || !Array.isArray(options)) return false;
	const selectedIds = Object.keys(parametrics).map(Number);
	return (options as readonly { ParametricId?: unknown }[]).some(
		(option) =>
			typeof option?.ParametricId === "number" &&
			selectedIds.includes(option.ParametricId)
	);
};

/** A Storyblok option field as a list: multi-option, single-option or unset. */
const toSectionList = (value: unknown): string[] => {
	if (typeof value === "string") return value ? [value] : [];
	if (!Array.isArray(value)) return [];
	return value.filter((item): item is string => typeof item === "string");
};

/**
 * The listing data without the filter groups named in Storyblok's
 * `hidden_filters`: the page's own story's list, else the layout's.
 */
export const omitHiddenFilterSections = <T extends { filters: GroupedFilters }>(
	data: T,
	layoutHidden: unknown,
	storyHidden: unknown
): T => {
	const own = toSectionList(storyHidden);
	const hidden = own.length ? own : toSectionList(layoutHidden);
	if (!hidden.length) return data;
	const filters = Object.fromEntries(
		Object.entries(data.filters ?? {}).filter(
			([name]) => !hidden.some((listed) => matchesSection(name, listed))
		)
	) as GroupedFilters;
	return { ...data, filters };
};
